// Verwaltung › Vereine: Liste mit Detail-Seitenleiste (am Handy Liste → Vereinsseite).
// Muster ~/Documents/TDA-Muster/Muster_Vereine_Verwaltung.html, Variante A, Entscheidung 22.10.2026.
// Daten: GET /api/vereine (Bestand mit Kennzahlen, nur eigener Verband), GET /api/vereine/archived.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../components/common/Toast';
import '../../styles/vereine.css';
import {
  apiJson, istVerein, istPlatzhalter, statusVon, datum, loginRelativ, plzOrt, anschrift,
} from './vereine/vereinHilfen';
import {
  Sym, Siegel, StatusAbzeichen, Fehlt, LeerKarte,
  SperrenDialog, FreigebenDialog, ArchivDialog, ZurueckholenDialog, LoeschenDialog,
} from './vereine/VereinTeile';
import { useVereinAktionen } from './vereine/useVereinAktionen';

const SORTEN = [
  ['name', 'Name A–Z'], ['ort', 'Ort A–Z'], ['wk', 'Meiste Wettkämpfer'],
  ['login', 'Zuletzt angemeldet'], ['neu', 'Zuerst angelegt'],
];
const STATI = [['aktiv', 'Aktiv (Login ≤ 60 Tage)'], ['ruhig', 'Ruhig'], ['nie', 'Nie angemeldet'], ['gesperrt', 'Gesperrt']];

const zahl = (x) => Number(x) || 0;
const istHandy = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 760px)').matches;

function sortiere(liste, sort) {
  const wert = (v) => {
    if (sort === 'wk') return -zahl(v.anzahl_wettkaempfer);
    if (sort === 'login') return v.letzter_login ? -new Date(v.letzter_login).getTime() : 0;
    if (sort === 'ort') return String(v.ort || '~').toLowerCase();
    if (sort === 'neu') return v.erstellt_am ? new Date(v.erstellt_am).getTime() : 0;
    return String(v.name || '').toLowerCase();
  };
  return [...liste].sort((a, b) => {
    const x = wert(a), y = wert(b);
    if (x < y) return -1;
    if (x > y) return 1;
    return String(a.name || '').localeCompare(String(b.name || ''), 'de');
  });
}

function csvExport(liste) {
  const kopf = ['Name', 'Kontoart', 'PLZ', 'Ort', 'Ansprechpartner', 'E-Mail', 'Telefon', 'Wettkämpfer', 'Meldungen', 'Letzter Login', 'Status'];
  const feld = (x) => `"${String(x ?? '').replace(/"/g, '""')}"`;
  const zeilen = liste.map((v) => [
    v.name, istVerein(v) ? 'Verein' : 'Verwaltung', v.plz, v.ort, v.ansprechpartner, v.email, v.telefon,
    zahl(v.anzahl_wettkaempfer), zahl(v.anzahl_anmeldungen), datum(v.letzter_login), statusVon(v)[1],
  ].map(feld).join(';'));
  const blob = new Blob(['﻿' + [kopf.map(feld).join(';'), ...zeilen].join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `vereine-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function LadeZeilen() {
  return (
    <div className="vv-zeilen" aria-busy="true" aria-label="Vereine werden geladen">
      {Array.from({ length: 7 }, (_, i) => (
        <div className="vv-zeile" style={{ cursor: 'default' }} key={i}>
          <span className="vv-skel" style={{ width: 38, height: 38 }} />
          <div><span className="vv-skel" style={{ height: 13, width: '55%', marginBottom: 6 }} /><span className="vv-skel" style={{ height: 10, width: '30%' }} /></div>
          <span className="vv-skel vv-z-zahl" style={{ height: 20 }} />
          <span className="vv-skel vv-z-zahl" style={{ height: 20 }} />
          <span className="vv-skel vv-z-login" style={{ height: 20 }} />
          <span className="vv-skel" style={{ height: 20, width: 90 }} />
          <span />
        </div>
      ))}
    </div>
  );
}

function Zeile({ v, gewaehlt, onWahl }) {
  return (
    <button type="button" className={`vv-zeile${gewaehlt ? ' vv-gewaehlt' : ''}${v.status === 'gesperrt' ? ' vv-zu' : ''}`} onClick={() => onWahl(v)} aria-current={gewaehlt ? 'true' : undefined}>
      <Siegel verein={v} />
      <span className="vv-z-name">
        <strong>{v.name}</strong>
        <span className="vv-z-unter">
          {istVerein(v) ? (plzOrt(v) || <Fehlt>Ort fehlt</Fehlt>) : <span className="vv-art">Verwaltungskonto</span>}
        </span>
      </span>
      <span className="vv-z-zahl"><b>{zahl(v.anzahl_wettkaempfer)}</b><small>Kämpfer</small></span>
      <span className="vv-z-zahl"><b>{zahl(v.anzahl_anmeldungen)}</b><small>Meld.</small></span>
      <span className="vv-z-login">
        {v.letzter_login ? <>{loginRelativ(v.letzter_login)}<small>{datum(v.letzter_login)}</small></> : <Fehlt>noch nie</Fehlt>}
      </span>
      <span><StatusAbzeichen verein={v} /></span>
      <span className="vv-pfeil"><Sym n="rechts" /></span>
    </button>
  );
}

function Seitenleiste({ v, onOeffnen, onBearbeiten, onMelden, onDialog }) {
  if (!v) return <aside className="vv-leiste"><div className="vv-l-leer">Verein links wählen.</div></aside>;
  const adr = anschrift(v);
  const gesperrt = v.status === 'gesperrt';
  return (
    <aside className="vv-leiste" aria-label={`Verein ${v.name}`}>
      <div className="vv-l-kopf">
        <Siegel verein={v} />
        <div style={{ minWidth: 0 }}>
          <h2>{v.name}</h2>
          <div className="vv-unter"><StatusAbzeichen verein={v} />{v.erstellt_am && <span>angelegt {datum(v.erstellt_am)}</span>}</div>
        </div>
      </div>
      {gesperrt && (
        <div className="vv-band vv-rot"><Sym n="schloss" /><div><b>Zugang gesperrt{v.gesperrt_am ? ` seit ${datum(v.gesperrt_am)}` : ''}</b>{v.gesperrt_grund || 'ohne Angabe'}</div></div>
      )}
      <div className="vv-l-zahlen">
        <div><b>{zahl(v.anzahl_wettkaempfer)}</b><small>Wettkämpfer</small></div>
        <div><b>{zahl(v.anzahl_anmeldungen)}</b><small>Meldungen</small></div>
        <div><b>{zahl(v.anzahl_turniere)}</b><small>Turniere</small></div>
      </div>
      <div className="vv-l-teil">
        <h3>Kontakt</h3>
        <dl className="vv-kv">
          <dt><Sym n="person" /></dt><dd>{v.ansprechpartner || <Fehlt>kein Ansprechpartner</Fehlt>}</dd>
          <dt><Sym n="mail" /></dt><dd>{v.email ? <a href={`mailto:${v.email}`}>{v.email}</a> : <Fehlt>keine E-Mail</Fehlt>}</dd>
          <dt><Sym n="tel" /></dt><dd>{v.telefon || <Fehlt>keine Nummer</Fehlt>}</dd>
          <dt><Sym n="ort" /></dt><dd>{adr || <Fehlt>keine Anschrift</Fehlt>}</dd>
          {v.homepage && <><dt><Sym n="welt" /></dt><dd>{v.homepage}</dd></>}
        </dl>
      </div>
      <div className="vv-l-teil">
        <h3>Konto</h3>
        <dl className="vv-kv">
          <dt><Sym n="schloss" /></dt><dd>{v.benutzername || <Fehlt>kein Benutzername</Fehlt>}<small>Benutzername</small></dd>
          <dt><Sym n="kal" /></dt><dd>{v.letzter_login ? <>{datum(v.letzter_login)}<small>letzter Login</small></> : <Fehlt>noch nie angemeldet</Fehlt>}</dd>
          {zahl(v.dojo_verknuepft) > 0 && <><dt><Sym n="verbind" /></dt><dd>mit Dojosoftware verknüpft<small>Wettkämpfer werden abgeglichen</small></dd></>}
        </dl>
      </div>
      <div className="vv-l-knoepfe">
        <button className="vv-k vv-k-haupt" onClick={() => onOeffnen(v)}>Verein öffnen <Sym n="rechts" /></button>
        <button className="vv-k" onClick={() => onBearbeiten(v)}><Sym n="stift" /> Bearbeiten</button>
        {istVerein(v) && <button className="vv-k" onClick={() => onMelden(v)}><Sym n="pokal" /> Zum Turnier melden</button>}
      </div>
      {istVerein(v) && (
        <div className="vv-l-gefahr">
          {gesperrt
            ? <button className="vv-k vv-k-klein" onClick={() => onDialog('freigeben', v)}><Sym n="offen" /> Freigeben</button>
            : <button className="vv-k vv-k-klein" onClick={() => onDialog('sperren', v)}><Sym n="schloss" /> Sperren</button>}
          <button className="vv-k vv-k-klein" onClick={() => onDialog('archiv', v)}><Sym n="archiv" /> Archivieren</button>
          <button className="vv-k vv-k-klein vv-k-gefahr" onClick={() => onDialog('loeschen', v)}><Sym n="muell" /> Löschen</button>
        </div>
      )}
    </aside>
  );
}

export default function VereineListePage() {
  const navigate = useNavigate();
  const [bestand, setBestand] = useState([]);
  const [archiv, setArchiv] = useState([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState(null);
  const [archivFehler, setArchivFehler] = useState('');
  const [suche, setSuche] = useState('');
  const [art, setArt] = useState('verein');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('name');
  const [liste, setListe] = useState('aktiv');
  const [filterOffen, setFilterOffen] = useState(false);
  const [gewaehlt, setGewaehlt] = useState(null);
  const [dialog, setDialog] = useState(null); // { art, v }

  const laden1 = useCallback(async () => {
    setLaden(true); setFehler(null);
    try {
      const d = await apiJson('/api/vereine');
      setBestand((Array.isArray(d?.data) ? d.data : []).filter((v) => !istPlatzhalter(v)));
    } catch (e) {
      setFehler(e);
    } finally {
      setLaden(false);
    }
    try {
      const a = await apiJson('/api/vereine/archived');
      setArchiv(Array.isArray(a?.data) ? a.data : []);
      setArchivFehler('');
    } catch (e) {
      setArchiv([]);
      setArchivFehler(e.message);
    }
  }, []);
  useEffect(() => { laden1(); }, [laden1]);

  const aktion = useVereinAktionen();
  const vereineNur = useMemo(() => bestand.filter(istVerein), [bestand]);
  const konten = bestand.length - vereineNur.length;

  const gefiltert = useMemo(() => {
    const s = suche.trim().toLowerCase();
    const l = bestand.filter((v) => {
      if (art === 'verein' && !istVerein(v)) return false;
      if (art === 'konto' && istVerein(v)) return false;
      if (status && statusVon(v)[0] !== status) return false;
      if (s && !`${v.name} ${v.ort || ''} ${v.plz || ''} ${v.ansprechpartner || ''} ${v.email || ''} ${v.benutzername || ''}`.toLowerCase().includes(s)) return false;
      return true;
    });
    const sortiert = sortiere(l, sort);
    // Verwaltungskonten immer hinter den Vereinen, abgetrennt
    return [...sortiert.filter(istVerein), ...sortiert.filter((v) => !istVerein(v))];
  }, [bestand, suche, art, status, sort]);

  const gewaehlterVerein = useMemo(() => {
    if (!gefiltert.length) return null;
    return gefiltert.find((v) => v.vereins_id === gewaehlt) || gefiltert[0];
  }, [gefiltert, gewaehlt]);

  const filterAktiv = !!(suche || status || art !== 'verein');
  const zuruecksetzen = () => { setSuche(''); setStatus(''); setArt('verein'); };

  const wk = vereineNur.reduce((s, v) => s + zahl(v.anzahl_wettkaempfer), 0);
  const anm = vereineNur.reduce((s, v) => s + zahl(v.anzahl_anmeldungen), 0);
  const nie = vereineNur.filter((v) => statusVon(v)[0] === 'nie').length;
  const gesp = vereineNur.filter((v) => v.status === 'gesperrt').length;

  const oeffnen = (v, tab) => navigate(`/dashboard/vereine/${v.vereins_id}${tab ? `?tab=${tab}` : ''}`);
  const bearbeiten = (v) => navigate(`/dashboard/vereine/${v.vereins_id}/bearbeiten`);
  const wahl = (v) => { if (istHandy()) oeffnen(v); else setGewaehlt(v.vereins_id); };
  const zu = useCallback(() => setDialog(null), []);

  const nachAktion = async (meldung) => { setDialog(null); toast.ok(meldung); await laden1(); };

  const zeilen = [];
  let trennerGesetzt = false;
  gefiltert.forEach((v) => {
    if (!istVerein(v) && art === 'alle' && !trennerGesetzt) {
      trennerGesetzt = true;
      zeilen.push(<div className="vv-trenner" key="trenner">Verwaltungskonten · zählen nicht als Verein</div>);
    }
    zeilen.push(<Zeile key={v.vereins_id} v={v} gewaehlt={gewaehlterVerein?.vereins_id === v.vereins_id} onWahl={wahl} />);
  });

  let inhalt;
  if (laden) inhalt = <LadeZeilen />;
  else if (fehler) {
    inhalt = (
      <LeerKarte sym="wolke" fehler titel="Vereine konnten nicht geladen werden" text={`${fehler.message}. Deine Suche und die Filter bleiben erhalten.`} technisch={fehler.technisch}>
        <button className="vv-k vv-k-haupt" onClick={laden1}><Sym n="neu" /> Erneut versuchen</button>
      </LeerKarte>
    );
  } else if (liste === 'archiv') {
    if (archivFehler) inhalt = <LeerKarte sym="wolke" fehler titel="Archiv konnte nicht geladen werden" text={archivFehler}><button className="vv-k vv-k-haupt" onClick={laden1}><Sym n="neu" /> Erneut versuchen</button></LeerKarte>;
    else if (!archiv.length) inhalt = <LeerKarte sym="archiv" titel="Archiv ist leer" text="Archivierte Vereine verschwinden aus dem Bestand, bleiben aber hier mit Grund und Datum. Sie lassen sich zurückholen." />;
    else {
      inhalt = (
        <div className="vv-zeilen" style={{ marginTop: 14 }}>
          {archiv.map((a) => (
            <div className="vv-zeile" key={a.archiv_id} style={{ cursor: 'default', gridTemplateColumns: '38px minmax(0,1fr) 160px auto' }}>
              <Siegel verein={a} />
              <span className="vv-z-name"><strong>{a.name}</strong>
                <span className="vv-z-unter">{[plzOrt(a), `${zahl(a.anzahl_wettkaempfer)} Wettkämpfer`, a.ausgeblendet ? `${zahl(a.anzahl_anmeldungen)} Meldungen erhalten` : 'altes Archiv', `Grund: ${a.archivierungsgrund || 'ohne Angabe'}`].filter(Boolean).join(' · ')}</span></span>
              <span className="vv-z-login">archiviert<small>{datum(a.archiviert_am)}{a.archiviert_von ? ` · ${a.archiviert_von}` : ''}</small></span>
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button className="vv-k vv-k-klein" onClick={() => setDialog({ art: 'zurueck', v: a })}><Sym n="neu" /> Zurückholen</button>
                <button className="vv-k vv-k-klein vv-k-gefahr" onClick={() => setDialog({ art: 'loeschenArchiv', v: a })}><Sym n="muell" /> Endgültig löschen</button>
              </span>
            </div>
          ))}
        </div>
      );
    }
  } else if (!bestand.length) {
    inhalt = (
      <LeerKarte sym="leute" titel="Noch keine Vereine" text="Vereine legen ihr Konto selbst über die Registrierung an, oder du trägst einen Verein hier ein. Erst danach können sie Wettkämpfer zu Turnieren melden.">
        <button className="vv-k vv-k-haupt" onClick={() => navigate('/dashboard/vereine/neu')}><Sym n="plus" /> Verein anlegen</button>
      </LeerKarte>
    );
  } else if (!gefiltert.length) {
    inhalt = (
      <LeerKarte sym="suche" titel={suche ? `Kein Verein passt zu „${suche}“` : 'Kein Verein passt zu diesen Filtern'} text="Prüfe die Schreibweise oder suche nach Ort, PLZ oder Ansprechpartner.">
        <button className="vv-k" onClick={zuruecksetzen}>Filter zurücksetzen</button>
      </LeerKarte>
    );
  } else {
    inhalt = (
      <div className="vv-raster">
        <div className="vv-zeilen">
          <div className="vv-kopfzeile" aria-hidden="true"><span /><span>Verein</span><span className="vv-r">Wettk.</span><span className="vv-r">Meld.</span><span className="vv-h-login">Letzter Login</span><span>Status</span><span /></div>
          {zeilen}
        </div>
        <Seitenleiste v={gewaehlterVerein} onOeffnen={(v) => oeffnen(v)} onBearbeiten={bearbeiten} onMelden={(v) => oeffnen(v, 'anmeldungen')}
          onDialog={(a, v) => setDialog({ art: a, v })} />
      </div>
    );
  }

  const d = dialog?.v;
  return (
    <div className="vv">
      <div className="vv-kopf">
        <div>
          <h1><span className="vv-strich" />Vereine</h1>
          <p>Alle Vereinskonten des Verbands. Klick auf einen Verein zeigt Kontakt, Wettkämpfer und Anmeldungen.</p>
        </div>
        <div className="vv-kopf-akt">
          <button className="vv-k" onClick={() => csvExport(gefiltert)} disabled={laden || !!fehler || !gefiltert.length}><Sym n="export" /> Liste exportieren</button>
          <button className="vv-k vv-k-haupt" onClick={() => navigate('/dashboard/vereine/neu')} aria-label="Neuer Verein"><Sym n="plus" /><span>Neuer Verein</span></button>
        </div>
      </div>

      {!laden && !fehler && (
        <div className="vv-zahlen">
          <button className="vv-zahl" aria-pressed={!status} onClick={() => setStatus('')}>
            <small>Vereine</small><b>{vereineNur.length}</b><span>{konten ? `${konten} Verwaltungskonten getrennt` : 'ohne Verwaltungskonten'}</span>
          </button>
          <div className="vv-zahl"><small>Wettkämpfer</small><b>{wk}</b><span>in {vereineNur.filter((v) => zahl(v.anzahl_wettkaempfer)).length} Vereinen dieses Verbands</span></div>
          <div className="vv-zahl"><small>Meldungen</small><b>{anm}</b><span>nicht storniert, alle Turniere</span></div>
          <button className={`vv-zahl${nie ? ' vv-warn' : ''}`} aria-pressed={status === 'nie'} onClick={() => setStatus(status === 'nie' ? '' : 'nie')}>
            <small>Nie angemeldet</small><b>{nie}</b><span>{gesp ? `${gesp} gesperrt` : 'Konto angelegt, nie eingeloggt'}</span>
          </button>
        </div>
      )}

      <div className={`vv-filter${filterOffen ? ' vv-offen' : ''}`}>
        <div className="vv-suche">
          <Sym n="suche" />
          <input type="search" placeholder="Name, Ort, Ansprechpartner, E-Mail" value={suche} onChange={(e) => setSuche(e.target.value)} aria-label="Vereine durchsuchen" />
          {suche && <button className="vv-suche-x" onClick={() => setSuche('')} aria-label="Suche leeren"><Sym n="x" /></button>}
        </div>
        <button className="vv-k vv-f-mehr" onClick={() => setFilterOffen(!filterOffen)} aria-expanded={filterOffen}><Sym n="filter" /> Filter{filterAktiv ? ' ·' : ''}</button>
        <div className="vv-seg vv-gross-nur" role="group" aria-label="Kontoart">
          <button aria-pressed={art === 'verein'} onClick={() => setArt('verein')}>Vereine <em>{vereineNur.length}</em></button>
          <button aria-pressed={art === 'konto'} onClick={() => setArt('konto')}>Verwaltung <em>{konten}</em></button>
          <button aria-pressed={art === 'alle'} onClick={() => setArt('alle')}>Alle</button>
        </div>
        <select className="vv-wahl vv-gross-nur" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">Jeder Status</option>
          {STATI.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
        <select className="vv-wahl vv-gross-nur" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sortierung">
          {SORTEN.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
        <span className="vv-anzahl">
          {!laden && !fehler && liste === 'aktiv' && <>{gefiltert.length} {gefiltert.length === 1 ? 'Eintrag' : 'Einträge'}{filterAktiv && <> · <button className="vv-zuruecksetzen" onClick={zuruecksetzen}>Filter zurücksetzen</button></>}</>}
        </span>
      </div>
      <div className="vv-reiter" role="tablist">
        <button role="tab" aria-selected={liste === 'aktiv'} onClick={() => setListe('aktiv')}>Bestand</button>
        <button role="tab" aria-selected={liste === 'archiv'} onClick={() => setListe('archiv')}>Archiv <em>{archiv.length}</em></button>
      </div>

      {inhalt}

      {dialog?.art === 'sperren' && <SperrenDialog verein={d} onSchliessen={zu} onOk={async (grund) => { await aktion.sperren(d, grund); await nachAktion('Zugang gesperrt'); }} />}
      {dialog?.art === 'freigeben' && <FreigebenDialog verein={d} onSchliessen={zu} onOk={async () => { await aktion.freigeben(d); await nachAktion('Zugang freigegeben. Der Verein kann sich wieder anmelden.'); }} />}
      {dialog?.art === 'archiv' && <ArchivDialog verein={d} wettkaempfer={zahl(d.anzahl_wettkaempfer)} meldungen={zahl(d.anzahl_anmeldungen)} onSchliessen={zu}
        onOk={async (grund) => { await aktion.archivieren(d, grund); await nachAktion('Verein archiviert. Er steht jetzt im Archiv.'); }} />}
      {dialog?.art === 'loeschen' && <LoeschenDialog verein={d} wettkaempfer={zahl(d.anzahl_wettkaempfer)} meldungen={zahl(d.anzahl_anmeldungen)} onSchliessen={zu}
        onArchivieren={() => setDialog({ art: 'archiv', v: d })}
        onOk={async (name) => { await aktion.loeschen(d.vereins_id, name); await nachAktion(`${d.name} endgültig gelöscht`); }} />}
      {dialog?.art === 'zurueck' && <ZurueckholenDialog eintrag={d} onSchliessen={zu} onOk={async () => { await aktion.zurueckholen(d); setListe('aktiv'); await nachAktion(`${d.name} ist wieder im Bestand`); }} />}
      {dialog?.art === 'loeschenArchiv' && <LoeschenDialog verein={d} archiviert wettkaempfer={zahl(d.anzahl_wettkaempfer)} meldungen={zahl(d.anzahl_anmeldungen)} onSchliessen={zu}
        onOk={async (name) => { await aktion.loeschen(d.original_vereins_id, name); await nachAktion(`${d.name} endgültig gelöscht`); }} />}
    </div>
  );
}

